/**
 * reCAPTCHA v3 (enterprise script) for the one anonymous form in the app that
 * sends mail — the email-only trial start. Same public site key and action
 * contract as the marketing site's forms; the API decides what an empty token
 * means (blocked by an extension, offline). The script is only injected when
 * the form mounts, never app-wide.
 */
const RECAPTCHA_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LdCD7wrAAAAAJL5ZpagTvlDriWSo0FVDAOM2mRY";

export const TRIAL_START_ACTION = "trial_start";

interface GrecaptchaEnterprise {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: GrecaptchaEnterprise };
  }
}

const SCRIPT_ID = "timeplanned-recaptcha";

/** Injects the reCAPTCHA script once. Call on mount of the form. */
export const loadRecaptcha = (): void => {
  if (typeof document === "undefined" || document.getElementById(SCRIPT_ID)) {
    return;
  }
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
  script.async = true;
  document.head.appendChild(script);
};

const waitForRecaptcha = async (): Promise<GrecaptchaEnterprise | undefined> => {
  for (let attempt = 0; attempt < 30; attempt++) {
    const enterprise = window.grecaptcha?.enterprise;
    if (enterprise) {
      return enterprise;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return undefined;
};

/** A token for one submission, or "" when reCAPTCHA is unavailable. */
export const getRecaptchaToken = async (action: string): Promise<string> => {
  loadRecaptcha();
  try {
    const grecaptcha = await waitForRecaptcha();
    if (!grecaptcha) {
      return "";
    }
    return await new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(reject);
      });
    });
  } catch {
    return "";
  }
};
